import React, { useRef, useEffect, useState } from "react";
import "../styles/carousel.css";
import ScrollInstruction from "../images/scrollinstruction.png";
import SwipeInstruction from "../images/swipeinstruction.png";
import { AnimatePresence, motion } from "framer-motion";
import { useAudio } from "../components/AudioContext";

export default function Carousel({ images, skillstate }) {
  const carouselRef = useRef(null);
  const [currentSkill, setCurrentSkill] = useState("");
  const previousSkill = useRef(null);
  const previousTouchY = useRef(null);
  const previousTargetRotation = useRef(0); // Track previous target rotation
  const [stateChanged, setStateChanged] = useState(false);

  let rotation = 0;
  let targetRotation = 0;

  const { playRotate, pauseRotate } = useAudio();

  useEffect(() => {
    const carouselItems =
      carouselRef.current.querySelectorAll(".carousel-item");
    const angleIncrement = (2 * Math.PI) / images.length;
    const radius = 300;

    const rotateCarousel = () => {
      const deltaRotation = (targetRotation - rotation) * 0.05; // Adjust rotation speed
      rotation += deltaRotation;

      carouselItems.forEach((item, index) => {
        const rotateAngle = rotation + angleIncrement * index;
        const x = Math.cos(rotateAngle) * radius;
        const y = Math.sin(rotateAngle) * radius;
        item.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${
          rotateAngle + Math.PI / 2
        }rad)`;
      });

      previousTargetRotation.current = targetRotation; // Update previous target rotation

      requestAnimationFrame(rotateCarousel);
    };

    const handleWheel = (event) => {
      event.preventDefault(); // Prevent default behavior (scrolling the page)

      const deltaY = event.deltaY;
      targetRotation += deltaY * 0.0025; // Adjust rotation speed based on deltaY

      // Calculate the current index based on rotation angle
      const angleIncrement = (2 * Math.PI) / images.length;
      const rotationAngle = (-Math.PI / 2 - rotation) % (2 * Math.PI);
      const index = Math.round(rotationAngle / angleIncrement);
      const normalizedIndex = (index + images.length) % images.length;
      setCurrentSkill(images[normalizedIndex].text);
    };

    const handleTouchStart = (event) => {
      previousTouchY.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      event.preventDefault(); // Prevent default touch behavior

      const touchY = event.touches[0].clientY;
      if (previousTouchY.current !== null) {
        const deltaY = touchY - previousTouchY.current;
        targetRotation += deltaY * 0.0025; // Adjust rotation speed based on touch movement
      }
      previousTouchY.current = touchY;

      const angleIncrement = (2 * Math.PI) / images.length;
      const rotationAngle = (-Math.PI / 2 - rotation) % (2 * Math.PI);
      const index = Math.round(rotationAngle / angleIncrement);
      const normalizedIndex = (index + images.length) % images.length;

      // Update the current skill
      setCurrentSkill(images[normalizedIndex].text);
    };

    carouselRef.current.addEventListener("wheel", handleWheel, {
      passive: false,
    });
    carouselRef.current.addEventListener("touchstart", handleTouchStart);
    carouselRef.current.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });

    rotateCarousel();

    return () => {
      if (carouselRef.current) {
        carouselRef.current.removeEventListener("wheel", handleWheel);
        carouselRef.current.removeEventListener("touchstart", handleTouchStart);
        carouselRef.current.removeEventListener("touchmove", handleTouchMove);
      }
    };
  }, [images]);

  useEffect(() => {
    setCurrentSkill(images[0].text);
    setStateChanged(true);
  }, [images]);

  useEffect(() => {
    if (!stateChanged && currentSkill !== previousSkill.current) {
      playRotate(1);
      previousSkill.current = currentSkill;
    } else {
      setStateChanged(false);
    }
  }, [currentSkill]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        whileInView={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        key={skillstate}
        className="circular-carousel-container"
      >
        <div ref={carouselRef} className="carousel">
          {images.map((image, index) => (
            <div key={index} className="carousel-item">
              <img
                src={image.image}
                alt="carousel"
                className="carousel-image"
              />
            </div>
          ))}
          <div className="glowball"></div>
          {window.innerWidth < 768 ? (
            <img src={SwipeInstruction} alt="swipe" className="instruction" />
          ) : (
            <img src={ScrollInstruction} alt="scroll" className="instruction" />
          )}
        </div>
        <div className="skill-at-90deg">{currentSkill}</div>
      </motion.div>
    </AnimatePresence>
  );
}
